import type { ResourceCategoryV2 } from "../fields/form-entity-select";

/**
 * Derives `FormEntitySelect`'s `valueCategory` for the decision/assignment **resource** (LHS) side.
 */
export function inferResourceCategoryFromEntityFields(s: {
	resourceReactionNodeId?: string;
	screenFieldId?: string;
	advancedTriggerId?: string;
	resourceCollectionId?: string;
	resourceFormulaVariableId?: string;
	formulaVariableName?: string;
	simpleVariableName?: string;
	databaseTableFieldId?: string;
}): ResourceCategoryV2 | undefined {
	if (s.resourceReactionNodeId) return "reactionResource";
	if (s.screenFieldId) return "screenField";
	if (s.advancedTriggerId) return "advancedTrigger";
	if (s.resourceCollectionId) return "collection";
	if (s.resourceFormulaVariableId || s.formulaVariableName) return "formulaVariable";
	if (s.simpleVariableName) return "simpleVariable";
	if (s.databaseTableFieldId) return "singleRowDbVariableField";
	return undefined;
}

/**
 * Derives `FormEntitySelect`’s `valueCategory` for display (pill / selectedMeta) from persisted value fields.
 * Aligns with `getValueCategory` in api-mapping-utils for variables.
 */
export function inferValueCategoryFromEntityFields(s: {
	valueCategory?: ResourceCategoryV2;
	valueGlobalConstant?: string;
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueAdvancedTriggerId?: string;
	valueResourceCollectionId?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueDatabaseSingleRowVariableId?: string;
	valueSimpleVariableName?: string;
	valueFormulaVariableName?: string;
	valueDatabaseSingleRowVariableName?: string;
}): ResourceCategoryV2 | undefined {
	if (s.valueCategory) return s.valueCategory;
	if (!isDecisionValueEntityRow(s)) return "literal";
	if (s.valueGlobalConstant) return "globalConstant";
	if (s.valueResourceCollectionId) return "collection";
	if (s.valueDatabaseTableFieldId) return "singleRowDbVariableField";
	if (s.valueScreenFieldId) return "screenField";
	if (s.valueAdvancedTriggerId) return "advancedTrigger";
	if (s.valueSimpleVariableId) return "simpleVariable";
	if (s.valueFormulaVariableId) return "formulaVariable";
	if (s.valueDatabaseSingleRowVariableId) return "databaseSingleRowVariable";
	if (s.valueFormulaVariableName) return "formulaVariable";
	if (s.valueDatabaseSingleRowVariableName) return "databaseSingleRowVariable";
	if (s.valueSimpleVariableName) return "simpleVariable";
	return "literal";
}

/**
 * True when the decision row's right-hand "value" is a structured entity (field, variable, etc.),
 * not free-typed plain text.
 */
export function isDecisionValueEntityRow(s: {
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueAdvancedTriggerId?: string;
	valueResourceCollectionId?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueDatabaseSingleRowVariableId?: string;
	valueGlobalConstant?: string;
	valueSimpleVariableName?: string;
	valueFormulaVariableName?: string;
	valueDatabaseSingleRowVariableName?: string;
}): boolean {
	if (
		s.valueDatabaseTableFieldId ||
		s.valueScreenFieldId ||
		s.valueAdvancedTriggerId ||
		s.valueResourceCollectionId ||
		s.valueSimpleVariableId ||
		s.valueFormulaVariableId ||
		s.valueDatabaseSingleRowVariableId ||
		s.valueGlobalConstant
	) {
		return true;
	}
	return Boolean(
		s.valueSimpleVariableName ||
			s.valueFormulaVariableName ||
			s.valueDatabaseSingleRowVariableName,
	);
}
